import { Grid } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import SelectField from 'components/select-field';
import { useUserInformationContext } from 'contexts/userContext';
import { useGetListings } from 'hooks/useGetListings';
import { PURCHASE, PURCHASE_STANDARD, REFINANCE } from 'utils/constants';

export type NewOrderValues = {
  transaction_type: string;
  type: string;
  workflow: string;
};

type Option = { code: string; description: string };

const WORKFLOWS_BY_TRANSACTION: Readonly<Record<string, readonly Option[]>> = {
  [PURCHASE]: [{ code: PURCHASE_STANDARD, description: PURCHASE_STANDARD }],
  [REFINANCE]: [{ code: REFINANCE, description: REFINANCE }],
};

/** Mirrors the create-order modal: the workflow list depends on the transaction type. */
export const workflowsFor = (transactionType: string): readonly Option[] => (
  WORKFLOWS_BY_TRANSACTION[transactionType] ?? []
);

export const agencyChoiceNeeded = (accessCount: number): boolean => accessCount > 1;

type NewOrderFieldsProps = {
  values: NewOrderValues;
  agencyId: string;
  onSetValue: (key: string, value: string) => void;
  onAgencyChange: (agencyId: string) => void;
};

export const NewOrderFields = ({ values, agencyId, onSetValue, onAgencyChange }: NewOrderFieldsProps) => {
  const { t } = useTranslation('imports');
  const { currentUser } = useUserInformationContext();
  const accesses = currentUser?.accesses ?? [];

  const { data: transactionsResponse } = useGetListings({ code: 'allowed_transactions' });
  const { data: typesResponse } = useGetListings({ code: 'allowed_types' });
  const transactionTypes = transactionsResponse?.getListing.entries ?? [];
  const types = typesResponse?.getListing.entries ?? [];
  const workflows = workflowsFor(values.transaction_type);

  const handleTransactionChange = (transactionType: string) => {
    const [firstWorkflow] = workflowsFor(transactionType);
    onSetValue('order.transaction_type', transactionType);
    onSetValue('order.workflow', firstWorkflow?.code ?? '');
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <SelectField
          name="transaction_type"
          value={values.transaction_type}
          data={transactionTypes}
          dataKey="code"
          dataText="description"
          dataValue="code"
          emptyValue={false}
          label={t('imports:target.transactionType')}
          handleChange={(event) => handleTransactionChange(String(event.target.value))}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <SelectField
          name="type"
          value={values.type}
          data={types}
          dataKey="code"
          dataText="description"
          dataValue="code"
          emptyValue={false}
          disabled={types.length === 0}
          label={t('imports:target.type')}
          handleChange={(event) => onSetValue('order.type', String(event.target.value))}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <SelectField
          name="workflow"
          value={values.workflow}
          data={[...workflows]}
          dataKey="code"
          dataText="description"
          dataValue="code"
          emptyValue={false}
          disabled={workflows.length === 0}
          label={t('imports:target.workflow')}
          handleChange={(event) => onSetValue('order.workflow', String(event.target.value))}
        />
      </Grid>
      {agencyChoiceNeeded(accesses.length) && (
        <Grid item xs={12} md={6}>
          <SelectField
            name="agency_id"
            value={agencyId}
            data={accesses}
            dataKey="_id"
            dataText="name"
            dataValue="_id"
            emptyValue={false}
            label={t('imports:target.agency')}
            handleChange={(event) => onAgencyChange(String(event.target.value))}
          />
        </Grid>
      )}
    </Grid>
  );
};
